'use client'

import { useEffect, useState } from 'react'

// Always shown this close to the top, so a page at rest never hides it.
const TOP_THRESHOLD_PX = 16
// Don't hide until the user has scrolled past the chrome itself.
const HIDE_AFTER_PX = 64
// Ignore jitter (momentum tails, sub-pixel scroll) in either direction.
const DIRECTION_DELTA_PX = 6

/**
 * Next hidden state for scroll-away chrome (the unified shell's mobile top
 * row): hide on a deliberate scroll down past the chrome, show again on any
 * deliberate scroll up, and always show near the top. Pure so it can be
 * tested without a DOM. Exported for tests.
 */
export function nextHiddenOnScroll(prevHidden: boolean, prevY: number, y: number): boolean {
  if (y <= TOP_THRESHOLD_PX) {
    return false
  }
  const delta = y - prevY
  if (delta > DIRECTION_DELTA_PX && y > HIDE_AFTER_PX) {
    return true
  }
  if (delta < -DIRECTION_DELTA_PX) {
    return false
  }
  return prevHidden
}

/** One scroll container's position and where its current one-direction run began. */
export interface ScrollRun {
  lastY: number
  anchorY: number
}

/**
 * Advance a container's scroll run. The anchor is where scrolling last changed
 * direction, so `nextHiddenOnScroll` judges the whole run rather than one
 * event. A smooth programmatic scroll, or a slow finger, moves a few pixels
 * per event; judged event by event it never crossed the jitter threshold, so
 * the row stayed up while the page slid underneath it (PROD-1524). Exported
 * for tests.
 */
export function nextScrollRun(run: ScrollRun | undefined, y: number): ScrollRun {
  if (!run) {
    return { lastY: y, anchorY: y }
  }
  const runDirection = Math.sign(run.lastY - run.anchorY)
  const direction = Math.sign(y - run.lastY)
  if (direction !== 0 && runDirection !== 0 && direction !== runDirection) {
    return { lastY: y, anchorY: run.lastY }
  }
  return { lastY: y, anchorY: run.anchorY }
}

/**
 * Whether scroll-away chrome should be hidden right now. Listens to scroll on
 * the whole document in the capture phase, so it follows whichever element
 * actually scrolls — `<AppShellMain>`, or a page's own scroll root (Tasker's
 * My Tasks list) — without each page wiring a ref. Horizontal scrolling (a
 * board's columns) leaves `scrollTop` unchanged and is ignored, as is
 * scrolling inside dialogs, sheets and popovers, and inside any region marked
 * `data-shell-pin-chrome="true"` — a board whose columns scroll on their own,
 * where hiding the row would only leave an empty strip above content that
 * never moves up into it.
 */
export function useHideOnScroll(): boolean {
  const [hidden, setHidden] = useState(false)
  useEffect(() => {
    const runs = new WeakMap<Element, ScrollRun>()
    const onScroll = (event: Event) => {
      const target = event.target === document ? document.scrollingElement : event.target
      if (!(target instanceof Element)) {
        return
      }
      if (target.closest('[role="dialog"], [data-radix-popper-content-wrapper], [data-shell-pin-chrome="true"]')) {
        return
      }
      const y = target.scrollTop
      const run = nextScrollRun(runs.get(target), y)
      runs.set(target, run)
      setHidden((prev) => nextHiddenOnScroll(prev, run.anchorY, y))
    }
    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => document.removeEventListener('scroll', onScroll, { capture: true })
  }, [])
  return hidden
}
